// Endesga-32 palette — single source of truth for every UI color.
//
// Every visible color in the SPA must come from one of the 32 entries
// below, mirroring the same constraint applied to card art. We expose
// the palette in three forms:
//
//   1. Bare hex strings (`E32`) — for inline `style={{ color: ... }}`.
//   2. CSS custom properties (`E32_VARS`) — declared on `:root` so
//      anything that lives outside the theme object (raw CSS, DOM
//      attributes, third-party widgets) can still use `var(--e32-*)`.
//   3. Semantic vars (`E32_SEMANTIC_VARS`) — `--ink`, `--bg`, etc.
//      that resolve to a palette entry. Themes pick which entry each
//      semantic var maps to.
//
// Reference: https://lospec.com/palette-list/endesga-32

/** The 32 hex strings, in canonical Endesga-32 order. */
export const E32_HEX = [
  "#be4a2f", "#d77643", "#ead4aa", "#e4a672", "#b86f50", "#733e39",
  "#3e2731", "#a22633", "#e43b44", "#f77622", "#feae34", "#fee761",
  "#63c74d", "#3e8948", "#265c42", "#193c3e", "#124e89", "#0099db",
  "#2ce8f5", "#ffffff", "#c0cbdc", "#8b9bb4", "#5a6988", "#3a4466",
  "#262b44", "#181425", "#ff0044", "#68386c", "#b55088", "#f6757a",
  "#e8b796", "#c28569",
] as const;

/** Named handles for the semantic colors we lean on most. Use these
 *  inside theme files so the theme reads as words instead of indices. */
export const E32 = {
  rust: E32_HEX[0],
  burntOrange: E32_HEX[1],
  parchment: E32_HEX[2],
  tan: E32_HEX[3],
  brick: E32_HEX[4],
  oxblood: E32_HEX[5],
  plum: E32_HEX[6],
  ruby: E32_HEX[7],
  scarlet: E32_HEX[8],
  pumpkin: E32_HEX[9],
  amber: E32_HEX[10],
  butter: E32_HEX[11],
  fern: E32_HEX[12],
  forest: E32_HEX[13],
  pine: E32_HEX[14],
  midnightPine: E32_HEX[15],
  navy: E32_HEX[16],
  cobalt: E32_HEX[17],
  cyan: E32_HEX[18],
  white: E32_HEX[19],
  mist: E32_HEX[20],
  steel: E32_HEX[21],
  slate: E32_HEX[22],
  indigo: E32_HEX[23],
  midnight: E32_HEX[24],
  ink: E32_HEX[25],
  hotPink: E32_HEX[26],
  mulberry: E32_HEX[27],
  rose: E32_HEX[28],
  coral: E32_HEX[29],
  flesh: E32_HEX[30],
  almond: E32_HEX[31],
} as const;

/** Snap an arbitrary `#rrggbb` string to its nearest palette entry,
 *  measuring distance in plain RGB. Returns the original input if it
 *  isn't a recognizable 6-digit hex (defensive — calling code can
 *  hand us color tokens of any shape). */
export function snapToE32(hex: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return hex;
  const v = parseInt(m[1], 16);
  const r = (v >> 16) & 0xff;
  const g = (v >> 8) & 0xff;
  const b = v & 0xff;
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < E32_HEX.length; i++) {
    const e = E32_HEX[i];
    const er = parseInt(e.slice(1, 3), 16);
    const eg = parseInt(e.slice(3, 5), 16);
    const eb = parseInt(e.slice(5, 7), 16);
    const d = (er - r) ** 2 + (eg - g) ** 2 + (eb - b) ** 2;
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return E32_HEX[bestIdx];
}

/** CSS string declaring `--e32-0` … `--e32-31` plus the semantic vars
 *  the SPA reads. Inserted into a `<style>` block at startup. */
export function buildRootCss(): string {
  const indexed = E32_HEX.map((hex, i) => `  --e32-${i}: ${hex};`).join("\n");

  // Semantic vars — kept in sync with the JS-side `pixelTheme` so
  // anything reading either gets the same value. The CSS vars exist
  // primarily for code that can't (or shouldn't) wire through the
  // theme object: scrollbars, focus rings, native form controls.
  const semantic = `
  --ui-bg: ${E32.parchment};
  --ui-bg-deep: ${E32.parchment};
  --ui-page-bg: ${E32.parchment};
  --ui-panel: ${E32.white};
  --ui-panel-edge: ${E32.plum};
  --ui-panel-edge-strong: ${E32.ink};
  --ui-ink: ${E32.ink};
  --ui-ink-dim: ${E32.plum};
  --ui-ink-faint: ${E32.oxblood};
  --ui-accent: ${E32.ruby};
  --ui-good: ${E32.forest};
  --ui-bad: ${E32.rust};
  --ui-warn: ${E32.amber};
  --ui-danger-bg: ${E32.oxblood};
  --ui-danger-border: ${E32.ruby};
  --ui-danger-ink: ${E32.parchment};
`.trim();

  // Hover / press behavior for the canonical button system. Bitmap UI
  // snaps — no transition. Hover pops a 2 px ink shadow down-and-right;
  // press tucks it back and nudges 1 px so the button feels physical.
  const buttonRules = `
.doi-btn:hover:not(:disabled) {
  box-shadow: 2px 2px 0 0 ${E32.ink};
}
.doi-btn:active:not(:disabled) {
  box-shadow: none;
  transform: translate(1px, 1px);
}
`.trim();

  // App-wide focus ring. `:focus-visible` so mouse-clicks don't paint
  // a ring but keyboard tabs do.
  //
  // The ring is layered: a butter outline (3px, offset 2) sits inside
  // an ink box-shadow halo (5px). Butter pops on dark surfaces (cook
  // hearth, ink-tinted panels); ink pops on light surfaces (parchment
  // page bg, white panels). One of the two is always visible against
  // whatever the focused control happens to be sitting on. A single-
  // colour ring is unreliable in this palette: ink disappears on the
  // hearth's near-black, butter disappears on the parchment page bg.
  const focusRules = `
:focus { outline: none; }
:focus-visible {
  outline: 3px solid ${E32.butter};
  outline-offset: 2px;
  box-shadow: 0 0 0 5px ${E32.ink};
}
.doi-input:focus-visible {
  outline: 3px solid ${E32.butter};
  outline-offset: 0;
  box-shadow: 0 0 0 3px ${E32.ink};
}
.doi-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.doi-skip-link {
  position: absolute;
  left: 8px;
  top: -40px;
  z-index: 10000;
  padding: 8px 12px;
  background: ${E32.ink};
  color: ${E32.parchment};
  font-family: 'Press Start 2P', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-decoration: none;
  border: 2px solid ${E32.ink};
}
.doi-skip-link:focus { top: 8px; }
.doi-skip-link:focus-visible { outline: 2px solid ${E32.parchment}; outline-offset: 2px; }
`.trim();

  return `:root {\n${indexed}\n  ${semantic}\n}\n${buttonRules}\n${focusRules}\n`;
}
